import { useCallback, useEffect, useState } from 'react';
import {
  checkBaseUrlConnection,
  type BaseUrlConnectionResult,
} from '../api/checkBaseUrlConnection';
import { resolveAuthRefreshApiPathTail } from '../api/authRefresh';
import {
  BASE_URL,
  BASE_URL_DIRECT,
  LOGIN_BASE_URL,
  LOGIN_BASE_URL_DIRECT,
  USE_SPLIT_LOGIN_UPSTREAM,
} from '../baseUrl';
import { getApiTokenHealth, type ApiTokenHealth } from '../api/withApiAuth';
import { AssetRequestErrorPanel } from './AssetRequestErrorPanel';
import styles from './ConnectionTestPanel.module.css';

function formatUnixSeconds(sec: number | null): string {
  if (sec == null) {
    return 'n/a';
  }
  const d = new Date(sec * 1000);
  return `${d.toLocaleString()} (${d.toUTCString()})`;
}

function likelyFetchFailureHint(networkDetails?: string): string {
  if (!networkDetails || !networkDetails.includes('Failed to fetch')) {
    return '';
  }
  if (BASE_URL.startsWith('/')) {
    return 'Browser fetch failed before HTTP response. In proxy mode this usually means the dev proxy/server is not running, proxy target is unreachable, or upstream TLS handshake failed in the proxy.';
  }
  return `Browser fetch failed before HTTP response while calling ${BASE_URL_DIRECT} directly. This is commonly CORS or TLS certificate failure in the browser (the server cert is expired unless verification is bypassed).`;
}

type ConnectionTestPanelProps = {
  autoRun?: boolean;
};

export function ConnectionTestPanel({ autoRun = false }: ConnectionTestPanelProps) {
  const [checking, setChecking] = useState(false);
  const [lastResult, setLastResult] = useState<BaseUrlConnectionResult | null>(null);
  const [tokenHealth, setTokenHealth] = useState<ApiTokenHealth | null>(null);
  const [tokenHealthLoading, setTokenHealthLoading] = useState(false);
  const [publicIp, setPublicIp] = useState<string | null>(null);

  const runCheck = useCallback(async () => {
    setChecking(true);
    setLastResult(null);
    try {
      const result = await checkBaseUrlConnection();
      setLastResult(result);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (autoRun) {
      void runCheck();
    }
  }, [autoRun, runCheck]);

  useEffect(() => {
    let cancelled = false;
    setTokenHealth(null);
    setTokenHealthLoading(true);
    void getApiTokenHealth().then((h) => {
      if (!cancelled) {
        setTokenHealth(h);
        setTokenHealthLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!lastResult || lastResult.ok) {
      setPublicIp(null);
      return;
    }

    let cancelled = false;
    setPublicIp(null);
    void (async () => {
      try {
        const res = await fetch('https://api.ipify.org?format=json', { cache: 'no-store' });
        if (cancelled) {
          return;
        }
        if (!res.ok) {
          setPublicIp(`lookup failed (HTTP ${res.status})`);
          return;
        }
        const body = (await res.json()) as { ip?: unknown };
        if (typeof body.ip === 'string' && body.ip.trim()) {
          setPublicIp(body.ip.trim());
          return;
        }
        setPublicIp('lookup failed (missing ip field)');
      } catch (err) {
        if (cancelled) {
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        setPublicIp(`lookup failed (${msg})`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lastResult]);

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <button type="button" className={styles.runButton} disabled={checking} onClick={() => void runCheck()}>
          {checking ? 'Checking…' : 'Run test again'}
        </button>
      </div>

      <p className={styles.intro}>
        Target: <strong>{BASE_URL}</strong>
        {BASE_URL.startsWith('/') ? (
          <>
            {' '}
            <span className={styles.note}>
              (proxies to <strong>{BASE_URL_DIRECT}</strong> in dev)
            </span>
          </>
        ) : null}
      </p>
      {USE_SPLIT_LOGIN_UPSTREAM ? (
        <p className={styles.intro}>
          Login / register / refresh: <strong>{LOGIN_BASE_URL}</strong>
          {LOGIN_BASE_URL.startsWith('/') ? (
            <>
              {' '}
              <span className={styles.note}>(proxies to {LOGIN_BASE_URL_DIRECT})</span>
            </>
          ) : null}
        </p>
      ) : null}

      <details className={styles.details}>
        <summary>Interpret results</summary>
        <ul className={styles.hintList}>
          <li>
            <strong>No HTTP status</strong> — transport error; check dev proxy and API host reachability.
          </li>
          <li>
            <strong>401 / invalid_token</strong> — Bearer rejected; refresh token or sign in again.
          </li>
          <li>
            <strong>403</strong> — authenticated but not permitted for this route.
          </li>
          <li>
            <strong>5xx</strong> — upstream server error.
          </li>
        </ul>
      </details>

      <section className={styles.card} aria-label="API Bearer token status">
        <h2 className={styles.cardTitle}>API Bearer token</h2>
        {tokenHealthLoading ? (
          <p className={styles.line}>Checking token…</p>
        ) : tokenHealth ? (
          <>
            <span
              className={`${styles.badge} ${
                tokenHealth.validForApi && tokenHealth.hasToken ? styles.badgeOk : styles.badgeBad
              }`}
            >
              {tokenHealth.hasToken
                ? tokenHealth.validForApi
                  ? 'Valid for API requests'
                  : 'Not valid for API (check exp)'
                : 'No token — calls are unauthenticated'}
            </span>
            <p className={styles.line}>
              <span className={styles.key}>Source: </span>
              {tokenHealth.sourceDescription}
            </p>
            {tokenHealth.hasToken ? (
              <p className={styles.line}>
                <span className={styles.key}>Bearer length: </span>
                {tokenHealth.bearerCharCount} characters
              </p>
            ) : null}
            {tokenHealth.expUnix != null ? (
              <p className={styles.line}>
                <span className={styles.key}>exp: </span>
                {formatUnixSeconds(tokenHealth.expUnix)}
              </p>
            ) : null}
            <p className={styles.line}>{tokenHealth.decodeNote}</p>
          </>
        ) : (
          <p className={styles.line}>Could not load token info.</p>
        )}
      </section>

      {checking && !lastResult ? (
        <p className={styles.status} role="status">
          Sending request to the API base URL…
        </p>
      ) : null}

      {lastResult && lastResult.ok && !checking ? (
        lastResult.httpStatus < 400 ? (
          <p className={styles.success} role="status">
            {`Connection OK (HTTP ${lastResult.httpStatus}${lastResult.httpStatusText ? ` ${lastResult.httpStatusText}` : ''}).`}
          </p>
        ) : (
          <div className={styles.warnBox} role="status">
            <p className={styles.warnHead}>
              {`HTTP ${lastResult.httpStatus}${lastResult.httpStatusText ? ` ${lastResult.httpStatusText}` : ''} — reachable, but denied.`}
            </p>
            {lastResult.rejectionHint ? <p className={styles.line}>{lastResult.rejectionHint}</p> : null}
          </div>
        )
      ) : null}

      {lastResult && !lastResult.ok ? <AssetRequestErrorPanel error={lastResult.error} /> : null}

      {lastResult && !lastResult.ok ? (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Debug details</h2>
          <p className={styles.line}>
            <span className={styles.key}>HTTP: </span>
            {lastResult.error.httpStatus != null
              ? `${lastResult.error.httpStatus}${lastResult.error.httpStatusText ? ` ${lastResult.error.httpStatusText}` : ''}`
              : '(none)'}
          </p>
          <p className={styles.line}>
            <span className={styles.key}>Likely reason: </span>
            {lastResult.error.errorDescription ||
              lastResult.error.networkDetails ||
              lastResult.error.message ||
              '(not available)'}
          </p>
          {likelyFetchFailureHint(lastResult.error.networkDetails) ? (
            <p className={styles.line}>{likelyFetchFailureHint(lastResult.error.networkDetails)}</p>
          ) : null}
          {lastResult.error.rawBody ? (
            <pre className={styles.pre}>{lastResult.error.rawBody}</pre>
          ) : null}
          <p className={styles.line}>
            <span className={styles.key}>Current public IP: </span>
            {publicIp ?? 'checking…'}
          </p>
        </section>
      ) : null}
    </div>
  );
}
